import { CalendarTodayOutlined as CalendarTodayOutlinedIcon } from '@mui/icons-material';
import { Button } from '@mui/material';
import { SidebarAccordion } from 'src/components/accordion';
import { ComponentTypes } from 'src/interfaces';
import { lang } from 'src/utils';

import {
  IScheduledSectionBackupComponent,
  IScheduledSectionComponent,
} from '../../ScheduleComponentPage.interface';
import { formatComponentSubtitle } from './ComponentAccordion.helper';
import { ButtonWrapper } from './ComponentAccordion.styled';
import { ComponentAccordionContent } from './modules/ComponentAccordionContent';
import { ComponentAccordionTitle } from './modules/ComponentAccordionTitle';

export interface IComponentAccordion {
  components: IScheduledSectionComponent[];
  isCodConfirmationOpen: boolean | undefined;
  disabledStartDates?: string[];
  options?: {
    enableBackup?: boolean;
    isOverriding?: boolean;
    showAdditionalComponentButton?: boolean;
    showComponentSubTitle?: boolean;
    enabledSwapButton?: boolean;
  };
  onRemoveComponent: (component: IScheduledSectionComponent) => void;
  onSwapComponent: (component: IScheduledSectionComponent) => void;
  onAddAdditionalComponent: () => void;
  onAddNewBackup: (component: IScheduledSectionComponent) => void;
  onDeleteBackup: (
    mainComponent: IScheduledSectionComponent,
    backupComponent: IScheduledSectionBackupComponent,
  ) => void;
  onReorderBackup: (
    mainComponent: IScheduledSectionComponent,
    oldIndex: number,
    newIndex: number,
  ) => void;
  onPreviewBackup: (backupComponent: IScheduledSectionBackupComponent) => void;
  handleUpdateComponent: (updatedComponent: IScheduledSectionComponent) => void;
  onClickEditProduct?: (component: IScheduledSectionComponent) => void;
}

export const ComponentAccordion = (props: IComponentAccordion) => {
  const {
    components,
    isCodConfirmationOpen,
    options,
    onRemoveComponent,
    onSwapComponent,
    onAddAdditionalComponent,
    onAddNewBackup,
    onDeleteBackup,
    onReorderBackup,
    onPreviewBackup,
    handleUpdateComponent,
    onClickEditProduct,
  } = props;
  const { showAdditionalComponentButton = true } = options ?? {};

  const disabledStartDates: string[] = components
    .map((component) => component.scheduleStartDate)
    .filter((startDate): startDate is string => Boolean(startDate));

  const items = components.map((component) => {
    const startDate = component.override
      ? component.override.start_date
      : component.scheduleStartDate;
    const endDate = component.override
      ? component.override.end_date
      : component.scheduleEndDate;

    const subTitle = options?.showComponentSubTitle
      ? formatComponentSubtitle(startDate, endDate)
      : '';

    return {
      titleComponent: (
        <ComponentAccordionTitle
          title={
            component.isOverridden && component.override?.component
              ? component.override.component.name
              : component.component?.name
          }
          subTitle={subTitle}
        />
      ),
      content: (
        <ComponentAccordionContent
          selectedComponent={component}
          isCodConfirmationOpen={isCodConfirmationOpen}
          disabledStartDates={disabledStartDates}
          options={{
            ...options,
            enabledSwapButton:
              component.allow_overrides &&
              component.component?.component_type?.name ===
                ComponentTypes.PROMOTIONAL_SPACE,
          }}
          onClickRemoveButton={() => onRemoveComponent(component)}
          onClickSwapButton={() => onSwapComponent(component)}
          onCLickAddBackupButton={() => onAddNewBackup(component)}
          onClickBackupRemoveButton={(backupComponent) =>
            onDeleteBackup(component, backupComponent)
          }
          onBackupSortEnd={(oldIndex, newIndex) =>
            onReorderBackup(component, oldIndex, newIndex)
          }
          onClickBackupName={onPreviewBackup}
          handleUpdateComponent={handleUpdateComponent}
          onClickEditProduct={onClickEditProduct}
        />
      ),
      defaultExpanded: true,
    };
  });

  return (
    <>
      <SidebarAccordion items={items} />

      {showAdditionalComponentButton && (
        <ButtonWrapper>
          <Button
            variant="text"
            onClick={onAddAdditionalComponent}
            startIcon={<CalendarTodayOutlinedIcon />}>
            {components.length > 0
              ? lang.scheduleComponent.scheduleAdditionalComponent
              : lang.scheduleComponent.label}
          </Button>
        </ButtonWrapper>
      )}
    </>
  );
};
